import { AvatarOwnProps, Stack, StackProps, Tooltip, Typography, TypographyProps } from '@mui/material'
import isNil from 'lodash/isNil'
import { ReactNode, type ImgHTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { InitialsAvatar } from './avatar/InitialsAvatar'

export interface Props extends StackProps {
  text: string
  imgUrl?: string | null
  imgProps?: ImgHTMLAttributes<HTMLImageElement>
  /** Renders in place of the initials avatar — e.g. an integration or Omnea AI avatar. */
  avatar?: ReactNode
  /** `circular` for human users, `square` for non-human entities (suppliers, products, integrations, etc). */
  avatarVariant: 'square' | 'circular'
  /** Defaults to the theme's own 24px. Drop to 16 or 20 to sit beside `small-regular` text. */
  avatarSize?: AvatarOwnProps['size']
  tooltipLabel?: string
  disableTooltip?: boolean
  userId: string | null
  textProps?: TypographyProps
  isArchived?: boolean
}

export const ProfileBox = ({
  text,
  imgUrl,
  imgProps,
  avatar,
  tooltipLabel,
  avatarVariant,
  avatarSize,
  disableTooltip = false,
  userId,
  textProps,
  isArchived,
  ...stackProps
}: Props) => {
  const { user: loggedInUser } = useAuthenticatedUser()
  const { t: tCommon } = useTranslation('common')
  const isLoggedInUser = loggedInUser?.id === userId
  const isUserAvatar = !isNil(userId)
  // Default grey for other users; passed through InitialsAvatar's own sx so the
  // email/id hash (applied inside the leaf) wins over it when the flag is on.
  const otherUserBackground = !isLoggedInUser && isUserAvatar ? { backgroundColor: ' grey' } : {}

  const displayText = isArchived ? `${text} ${tCommon('archived')}` : text

  return (
    <Tooltip title={!disableTooltip ? tooltipLabel || text : ''}>
      <Stack
        direction="row"
        spacing={2}
        {...stackProps}
        sx={{ alignItems: 'center', overflow: 'hidden', textOverflow: 'ellipsis', ...stackProps.sx }}
      >
        {avatar ?? (
          <InitialsAvatar
            alt={text}
            src={imgUrl ? imgUrl.replace('http://', 'https://') : undefined}
            slotProps={{ img: imgProps }}
            variant={avatarVariant}
            size={avatarSize}
            fullName={text}
            colourSubjectId={userId}
            isArchived={isArchived}
            sx={{ lineHeight: '25px', '& .MuiAvatar-img': { objectFit: 'contain' }, ...otherUserBackground }}
          />
        )}
        <Typography variant="body-regular" noWrap {...textProps} {...(isArchived && { color: 'text.danger.default' })}>
          {displayText}
        </Typography>
      </Stack>
    </Tooltip>
  )
}
